import { useMemo } from 'react';
import { AutomationActionType } from '../../../types/enums/AutomationActionType';
import { useShouldRequestToDeployFilter } from '../../common/deploy-button/useShouldRequestToDeploy';
import { AutomationActionDescriptionByType } from '../helper';
import { AutomationFiltersTypesMap } from './filtersTypes';
import { Filters, FilterComponentType } from '../../common/filters/filters-renderer/types';
import { type Dictionary } from 'lodash';
import { getStepsFromAutomation, getRecordTypesFromAutomation } from '../helper';
import { uniqueByKey } from '../../../lib/uniqueByKey';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import useObjectTypesWithFetch from '../../../hooks/useObjectTypesWithFetch';
import { useAutomationFiltersContext } from './AutomationFiltersContext';
import { getRecordTypeIdentifier } from '../helper';

export const activeFilterItems = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

export const getActionFilterItems = (automations: AutomationStructureNew[]) => {
  const items: { value: AutomationActionType; label: string }[] = [];

  const actionsInUse = new Set(
    automations
      .map(({ automationDetails: { actions } }) =>
        actions.map(({ actionType }) => actionType as AutomationActionType),
      )
      .flat(),
  );

  actionsInUse?.forEach((action: AutomationActionType) => {
    if (AutomationActionDescriptionByType[action]) {
      items.push({ value: action, label: AutomationActionDescriptionByType[action] });
    }
  });

  return items;
};

export const useGetAssignmentFilters = (
  automations: AutomationStructureNew[] = [],
  objectTypeByName: Dictionary<ObjectTypeName>,
) => {
  const requestToDeployFilter = useShouldRequestToDeployFilter(automations);

  const objects = useMemo(() => {
    return uniqueByKey(
      automations.map((automation) => ({
        label: objectTypeByName[automation.objectName]?.label ?? automation.objectName,
        value: automation.objectName,
      })),
      (item) => item.value,
    );
  }, [automations, objectTypeByName]);

  const recordTypes = useMemo(() => {
    return uniqueByKey(
      automations.map((automation) => getRecordTypesFromAutomation(automation)).flat(),
      (item) => item.value,
    ).map((item) => ({
      label: item.label,
      value: item.value,
      labelDecoration: <ObjectTypeLabelAndKeyTag objectType={item.objectName ?? ''} />,
    }));
  }, [automations]);

  const steps = useMemo(() => {
    return uniqueByKey(
      automations.map((automation) => getStepsFromAutomation(automation.automationDetails)).flat(),
      (item) => item.value,
    );
  }, [automations]);

  const filters = useMemo(() => {
    let assignmentRuleFilters: Filters = {
      [AutomationFiltersTypesMap.object]: {
        type: FilterComponentType.MULTI_SELECT,
        items: objects,
        label: 'Object',
      },
      [AutomationFiltersTypesMap.recordTypes]: {
        type: FilterComponentType.MULTI_SELECT,
        items: recordTypes,
        label: 'Record Types',
      },
      [AutomationFiltersTypesMap.steps]: {
        type: FilterComponentType.MULTI_SELECT,
        items: steps,
        label: 'Steps',
      },
      [AutomationFiltersTypesMap.status]: {
        type: FilterComponentType.MULTI_SELECT,
        items: activeFilterItems,
        label: 'Status',
      },
    };
    if (requestToDeployFilter) {
      assignmentRuleFilters = {
        ...assignmentRuleFilters,
        ...requestToDeployFilter,
      };
    }
    return assignmentRuleFilters;
  }, [requestToDeployFilter, objects, recordTypes, steps]);
  return filters;
};

export const useGetScheduledAssignmentFilters = (
  automations: AutomationStructureNew[] = [],
  objectTypeByName: Dictionary<ObjectTypeName>,
) => {
  const requestToDeployFilter = useShouldRequestToDeployFilter(automations);
  const objects = useMemo(() => {
    return uniqueByKey(
      automations.map((automation) => ({
        label: objectTypeByName[automation.objectName]?.label ?? automation.objectName,
        value: automation.objectName,
      })),
      (item) => item.value,
    );
  }, [automations, objectTypeByName]);

  const filters = useMemo(() => {
    let assignmentRuleFilters: Filters = {
      [AutomationFiltersTypesMap.object]: {
        type: FilterComponentType.MULTI_SELECT,
        items: objects,
        label: 'Object',
      },
      [AutomationFiltersTypesMap.status]: {
        type: FilterComponentType.MULTI_SELECT,
        items: activeFilterItems,
        label: 'Status',
      },
    };

    if (requestToDeployFilter) {
      assignmentRuleFilters = {
        ...assignmentRuleFilters,
        ...requestToDeployFilter,
      };
    }
    return assignmentRuleFilters;
  }, [requestToDeployFilter, objects]);
  return filters;
};

export const getRecordTypesFromAutomations = (
  automations: AutomationStructureNew[],
  filterByObject: string[],
) => {
  const automationsByObject = filterByObject?.length
    ? automations.filter((automation) => filterByObject?.includes(automation.objectName))
    : automations;

  return uniqueByKey(
    automationsByObject.map((automation) => getRecordTypesFromAutomation(automation)).flat(),
    (item) => item.value,
  ).map((item) => ({
    label: item.label,
    value: item.value,
    labelDecoration: <ObjectTypeLabelAndKeyTag objectType={item.objectName ?? ''} />,
  }));
};

export const getStepsFromAutomations = (
  automations: AutomationStructureNew[],
  filterByObject: string[],
  filterByRecordTypes: string[],
) => {
  const automationsByObject = filterByObject?.length
    ? automations.filter((automation) => filterByObject?.includes(automation.objectName))
    : automations;

  const automationsByRecordTypes = filterByRecordTypes?.length
    ? automationsByObject.filter((automation) =>
        filterByRecordTypes.includes(
          getRecordTypeIdentifier(automation.funnelRecordType?.name ?? '', automation.objectName),
        ),
      )
    : automationsByObject;

  return uniqueByKey(
    automationsByRecordTypes
      .map((automation) => getStepsFromAutomation(automation.automationDetails))
      .flat(),
    (item) => item.value,
  );
};

export const useGetAutomationFilters = ({
  automations,
  crmOrgId,
}: {
  automations: AutomationStructureNew[];
  crmOrgId: string;
}) => {
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId });
  const { filterValues } = useAutomationFiltersContext();
  const filterByObject = filterValues.object?.selectedValues;
  const filterByRecordTypes = filterValues.recordTypes?.selectedValues;

  const requestToDeployFilter = useShouldRequestToDeployFilter(automations);

  const objects = useMemo(() => {
    return uniqueByKey(
      automations
        .map((automation) => ({
          label: objectTypesByName[automation.objectName]?.label ?? automation.objectName,
          value: automation.objectName,
        }))
        .filter((automation) => automation.value !== ''),
      (item) => item.value,
    );
  }, [automations, objectTypesByName]);

  const recordTypes = useMemo(
    () => getRecordTypesFromAutomations(automations, filterByObject),
    [automations, filterByObject],
  );

  const steps = useMemo(
    () => getStepsFromAutomations(automations, filterByObject, filterByRecordTypes),
    [automations, filterByObject, filterByRecordTypes],
  );

  const automationsFilters = useMemo((): Filters => {
    let regularFilters: Filters = {
      [AutomationFiltersTypesMap.object]: {
        type: FilterComponentType.MULTI_SELECT,
        items: objects,
        label: 'Object',
      },
      [AutomationFiltersTypesMap.recordTypes]: {
        type: FilterComponentType.MULTI_SELECT,
        items: recordTypes,
        label: 'Record Types',
      },
      [AutomationFiltersTypesMap.steps]: {
        type: FilterComponentType.MULTI_SELECT,
        items: steps,
        label: 'Steps',
      },
      [AutomationFiltersTypesMap.actionTypes]: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Action',
        items: getActionFilterItems(automations),
      },
      [AutomationFiltersTypesMap.status]: {
        type: FilterComponentType.MULTI_SELECT,
        label: 'Status',
        items: activeFilterItems,
      },
    };
    if (requestToDeployFilter) {
      regularFilters = {
        ...regularFilters,
        ...requestToDeployFilter,
      };
    }

    return regularFilters;
  }, [automations, requestToDeployFilter, objects, recordTypes, steps]);

  return automationsFilters;
};
